import { useContext, useEffect, useState } from 'react';

import { Button } from '@selina-finance/ui';
import { useNavigate } from 'react-router-dom';

import { ApplicationContext } from '@src/app/contexts/Application';
import { DebtConContext } from '@src/app/contexts/DebtCon';
import { useSentryLogger } from '@src/app/hooks/log';
import BackButton from '@src/ui/components/BackButton/BackButton';
import ErrorView from '@src/ui/components/ErrorView/ErrorView';
import DebtCoins from '@src/ui/components/Icons/DebtCoins';
import DebtCoinsLeft from '@src/ui/components/Icons/DebtCoinsLeft';

const IntroView = () => {
    const navigate = useNavigate();
    const [error, setError] = useState<boolean | null>(null);

    const { runCreditCommitments, applicationId, getAppData } =
        useContext(ApplicationContext);

    const { creditCommitmentsData, fetchCreditCommitments } =
        useContext(DebtConContext);

    const { sentryLog } = useSentryLogger(
        applicationId || '',
        'DebtCon Intro view',
    );

    const runAndGetCreditCommitments = async () => {
        const response = await runCreditCommitments();

        if (!response.success) {
            setError(true);
            return;
        }

        const commitmentsResponse = await fetchCreditCommitments();

        if (
            !commitmentsResponse.success ||
            commitmentsResponse.data.length === 0
        ) {
            navigate('/eligibility/wait', {
                replace: true,
                state: { debtCon: true },
            });
        }
    };

    const handleRetry = async () => {
        setError(null);
        sentryLog('Retrying creditCommitment process');
        await runAndGetCreditCommitments();
    };

    useEffect(() => {
        const getCreditCommitments = async () => {
            if (
                getAppData ||
                !applicationId ||
                creditCommitmentsData?.length > 0
            ) {
                return;
            }

            sentryLog('creditCommitment started');

            await runAndGetCreditCommitments();
        };

        getCreditCommitments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationId, getAppData]);

    return (
        <div className='flex flex-col mx-auto max-w-400 w-full lg:max-w-none'>
            {error ? (
                <ErrorView onRetry={handleRetry} />
            ) : (
                <>
                    <div className='hidden lg:block mb-8'>
                        <BackButton />
                    </div>
                    <div className='text-xl text-midnight-200'>
                        <div className='flex flex-col lg:flex-row-reverse lg:justify-end lg:gap-80'>
                            <div className='flex justify-center mt-8 mb-10 lg:mt-0'>
                                <DebtCoinsLeft className='hidden lg:block' />
                                <DebtCoins className='block lg:hidden' />
                            </div>

                            <div className='lg:w-393'>
                                <p className='text-2xl text-midnight-400 leading-100 font-sora-bold lg:text-28 tracking-tighter'>
                                    Tell us about your borrowing
                                </p>
                                <div className='leading-6 mt-8 text-lg'>
                                    <p>
                                        Our customers{' '}
                                        <span className='font-bold'>
                                            pay £625 less* each month
                                        </span>{' '}
                                        on average after consolidating their
                                        debts with Selina.
                                    </p>
                                    <p className='pt-2'>
                                        Tell us about your borrowing to see if
                                        you can do the same.
                                    </p>
                                    <p className='font-bold pt-2'>
                                        This won't impact your eligibility or
                                        credit score.
                                    </p>

                                    <footer className='mt-8'>
                                        <Button
                                            size='large'
                                            color='primary'
                                            className='font-bold w-full text-lg'
                                            disabled={Boolean(error)}
                                            onClick={() =>
                                                navigate(
                                                    '/eligibility/debt-con/main-goal',
                                                )
                                            }
                                        >
                                            <span className='text-lg'>
                                                Get started
                                            </span>
                                        </Button>
                                        <p className='text-sm leading-120% mt-3 text-center'>
                                            Think carefully before securing
                                            other debts against your home.
                                        </p>
                                    </footer>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default IntroView;
